/**
 * Compare Reports Section JavaScript
 * Handles animations and interactions for the compare reports section
 * Updated to support multiple instances on the same page
 */

class CompareReports {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.sectionId = this.section.getAttribute('data-section-id') || this.section.id.replace('compare-reports-', '');
    this.progressBars = this.section.querySelectorAll('.compare-reports__progress-fill, .compare-reports__progress-fill-horizontal, .compare-reports__progress-fill-vertical');
    this.observer = null;
    this.init();
  }

  init() {
    this.setupIntersectionObserver();
    this.addProgressBarAnimations();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px 0px -100px 0px',
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.animateProgressBars();
          this.observer.unobserve(entry.target);
        }
      });
    }, options);

    const metricComparison = this.section.querySelector('.compare-reports__metric-comparison');
    if (metricComparison) {
      this.observer.observe(metricComparison);
    }

    // Also observe the scores section for compare-reports-scores
    const scoresSection = this.section.querySelector('.compare-reports__section');
    if (scoresSection && !metricComparison) {
      this.observer.observe(scoresSection);
    }
  }

  animateProgressBars() {
    // Check if this section has already been animated
    if (this.section.hasAttribute('data-animated')) {
      return; // Don't re-animate the same section
    }

    // Mark section as animated
    this.section.setAttribute('data-animated', 'true');

    // Start animations with staggered timing for smooth effect
    this.progressBars.forEach((bar, index) => {
      setTimeout(() => {
        bar.style.animationPlayState = 'running';
      }, index * 120);
    });
  }

  addProgressBarAnimations() {
    // Pause animations initially for this specific section
    this.progressBars.forEach(bar => {
      bar.style.animationPlayState = 'paused';
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    // Clean up animation state
    this.section.removeAttribute('data-animated');
  }
}

// Store instances to manage them properly
const compareReportsInstances = new Map();

// Function to initialize all compare-reports sections
function initializeCompareReportsSections() {
  const compareReportsSections = document.querySelectorAll('[id^="compare-reports-"]');

  compareReportsSections.forEach(section => {
    const sectionId = section.id;
    if (sectionId && !compareReportsInstances.has(sectionId)) {
      const instance = new CompareReports(section);
      compareReportsInstances.set(sectionId, instance);
    }
  });
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', initializeCompareReportsSections);

// Also initialize after a small delay to catch any dynamically loaded sections
setTimeout(initializeCompareReportsSections, 100);

// Handle Shopify section reloads (theme editor)
document.addEventListener('shopify:section:load', function(event) {
  // Match compare-reports-, compare-reports-header-, and compare-reports-scores-
  const sectionElement = event.target.querySelector('[id^="compare-reports-"]');
  if (sectionElement) {
    const sectionId = sectionElement.id;

    // Destroy existing instance if it exists
    if (compareReportsInstances.has(sectionId)) {
      compareReportsInstances.get(sectionId).destroy();
      compareReportsInstances.delete(sectionId);
    }

    // Create new instance
    const instance = new CompareReports(sectionElement);
    compareReportsInstances.set(sectionId, instance);
  }
});

// Handle section unload
document.addEventListener('shopify:section:unload', function(event) {
  const sectionElement = event.target.querySelector('[id^="compare-reports-"]');
  if (sectionElement) {
    const sectionId = sectionElement.id;
    if (compareReportsInstances.has(sectionId)) {
      compareReportsInstances.get(sectionId).destroy();
      compareReportsInstances.delete(sectionId);
    }
  }
});